import './ui/styles.css';
import { registerSW } from 'virtual:pwa-register';
import { mountApp } from './ui/app.js';
import { initTheme } from './ui/theme/theme.js';
import { armAutoUpdate } from './ui/update-manager.js';

initTheme();

const root = document.querySelector<HTMLElement>('#app');
if (root) mountApp(root);

/**
 * Coherent-IA Task 4: client update flow. `registerType: 'prompt'`
 * (vite.config.ts) holds a newly-fetched service worker in `waiting` instead
 * of silently activating it (that was 'autoUpdate''s problem — a returning
 * client kept serving the stale app shell until a reload, often two,
 * happened to land after the swap) and fires `onNeedRefresh` once a new
 * version is ready. `injectRegister: false` (vite.config.ts) stops the
 * plugin's own injected `<script>` from ALSO calling `registerSW` — this is
 * the only registration.
 *
 * Guarded on `__SWARASENSE_TEST__` (the Playwright screenshot harness, see
 * `audio/engine.ts`) so the periodic `reg.update()` polling below never
 * fires against the headless capture pages — harmless either way (in dev,
 * `virtual:pwa-register` no-ops registration entirely), but there's no
 * reason to run it there.
 */
if (typeof window !== 'undefined' && !window.__SWARASENSE_TEST__) {
  /** Connected-Swarasense (W8): poll frequently (~2 min) + on refocus so a new
   * publish reaches an open tab quickly, then auto-reload when idle. */
  const UPDATE_CHECK_INTERVAL_MS = 2 * 60 * 1000;

  const updateSW = registerSW({
    onNeedRefresh() {
      // Auto-apply when idle (never mid-take/mid-finder) — see update-manager.ts.
      armAutoUpdate(() => {
        void updateSW(true); // skipWaiting + reload
      });
    },
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return; // unsupported browser / registration failed — nothing to poll
      window.setInterval(() => void registration.update(), UPDATE_CHECK_INTERVAL_MS);
      const checkNow = (): void => {
        if (document.visibilityState === 'visible') void registration.update();
      };
      document.addEventListener('visibilitychange', checkNow);
      window.addEventListener('focus', checkNow);
    },
  });
}
